import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getCookie, setCookie, removeCookie, getAuthToken, setAuthToken, removeAuthToken } from "../../utils/cookieUtils";
import apiClient from "../../services/apiClient";

const getSavedUser = () => {
  try {
    const u = getCookie("user");
    return u ? JSON.parse(u) : null;
  } catch {
    return null;
  }
};

const hasTokenCookie = Boolean(getAuthToken());

export const fetchUserProfile = createAsyncThunk(
  "session/fetchUserProfile",
  async (forcedCompanyCode, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentUser = state.session?.settings?.userInfo;
      const companyCode =
        forcedCompanyCode ||
        state.executor?.companyInfo?.companyCode ||
        currentUser?.companyCode ||
        getCookie("companyCode") ||
        "";
      const role = currentUser?.role || getCookie("role") || "company_admin";
      const isEmployee = role === "employee" || role === "sales_rep";
      const employeeId = currentUser?.id || currentUser?._id || getCookie("userId");

      if (isEmployee && employeeId) {
        try {
          const res = await apiClient.get(`/api/employees?companyCode=${companyCode}`, {
            headers: { "x-active-company-code": companyCode },
          });
          if (res?.success && Array.isArray(res.employees)) {
            const matchedEmp = res.employees.find(
              (e) => String(e._id || e.id) === String(employeeId) || e.mobile === currentUser?.mobile
            );
            if (matchedEmp) {
              return {
                ...currentUser,
                ...matchedEmp,
                id: matchedEmp._id || matchedEmp.id || employeeId,
                role: "employee",
                companyCode,
                profilePhoto: matchedEmp.profilePhoto || currentUser?.profilePhoto || "",
                twoFactorEnabled: Boolean(matchedEmp.twoFactorEnabled),
              };
            }
          }
        } catch {
          // Fallback to current user
        }
        return currentUser;
      }

      // Admin / Company Profile
      const res = await apiClient.get(`/api/auth/company/${companyCode}`, {
        headers: { "x-active-company-code": companyCode },
      });

      if (res?.success && res.company) {
        const c = res.company;
        return {
          ...currentUser,
          companyName: c.companyName || currentUser?.companyName,
          companyCode: c.companyCode || companyCode,
          name: c.name || currentUser?.name || "Administrator",
          email: c.email || currentUser?.email,
          countryCode: c.countryCode || currentUser?.countryCode || "+91",
          mobile: c.mobile || currentUser?.mobile,
          industry: c.industry || currentUser?.industry,
          teamSize: Number(c.teamSize || currentUser?.teamSize || 20),
          companyAddress: c.companyAddress || currentUser?.companyAddress,
          profilePhoto: c.profilePhoto || currentUser?.profilePhoto || "",
          twoFactorEnabled: Boolean(c.twoFactorEnabled),
          createdAt: c.createdAt || currentUser?.createdAt,
          role: currentUser?.role || "company_admin",
        };
      }
      return currentUser;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch profile");
    }
  }
);

const initialState = {
  settings: {
    userInfo: getSavedUser(),
  },
  isAuthenticated: hasTokenCookie,
  loading: false,
  error: null,
};

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    setSession: (state, action) => {
      const { token, userInfo, user } = action.payload || {};
      const resolvedUser = userInfo !== undefined ? userInfo : (user || null);
      const resolvedId = resolvedUser?.id || resolvedUser?._id || action.payload?.id || null;
      const resolvedRole = resolvedUser?.role || action.payload?.role || "company_admin";
      const resolvedCompanyCode = resolvedUser?.companyCode || action.payload?.companyCode || null;
      const resolvedCompanyName = resolvedUser?.companyName || action.payload?.companyName || null;

      const completeUser = resolvedUser ? {
        ...resolvedUser,
        id: resolvedId,
        role: resolvedRole,
        companyCode: resolvedCompanyCode || resolvedUser.companyCode,
        companyName: resolvedCompanyName || resolvedUser.companyName,
      } : null;

      // Store JWT token exclusively in cookies under Authorization (never in localStorage or cache)
      if (token) setAuthToken(token, 7);
      if (completeUser) setCookie("user", JSON.stringify(completeUser), 7);
      if (resolvedId) setCookie("userId", resolvedId, 7);
      if (resolvedRole) setCookie("role", resolvedRole, 7);
      if (resolvedCompanyCode) setCookie("companyCode", resolvedCompanyCode, 7);
      if (resolvedCompanyName) setCookie("companyName", resolvedCompanyName, 7);

      state.settings = {
        ...state.settings,
        userInfo: completeUser,
      };
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
    },
    setUserInfo: (state, action) => {
      const resolvedUser = action.payload || null;
      if (resolvedUser) {
        setCookie("user", JSON.stringify(resolvedUser), 7);
        if (resolvedUser.companyCode) setCookie("companyCode", resolvedUser.companyCode, 7);
        if (resolvedUser.companyName) setCookie("companyName", resolvedUser.companyName, 7);
      }
      state.settings.userInfo = resolvedUser;
    },
    clearSession: (state) => {
      // Remove all session cookies including Authorization JWT token
      removeAuthToken();
      removeCookie("user");
      removeCookie("userId");
      removeCookie("role");

      state.settings = {
        userInfo: null,
      };
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
    setSessionLoading: (state, action) => {
      state.loading = action.payload;
    },
    setSessionError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.settings.userInfo = {
            ...state.settings.userInfo,
            ...action.payload,
          };
          setCookie("user", JSON.stringify(state.settings.userInfo), 7);
          if (action.payload.companyCode) setCookie("companyCode", action.payload.companyCode, 7);
          if (action.payload.companyName) setCookie("companyName", action.payload.companyName, 7);
          if (action.payload.companyAddress) setCookie("companyAddress", action.payload.companyAddress, 7);
        }
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSession,
  setUserInfo,
  clearSession,
  setSessionLoading,
  setSessionError,
} = sessionSlice.actions;

export default sessionSlice.reducer;
